import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorState } from './async-state';

type PageErrorBoundaryProps = {
  /** Name of the surface being guarded, used in the fallback copy. */
  title?: string;
  children: ReactNode;
};

type PageErrorBoundaryState = {
  error: Error | null;
};

/**
 * Stops one broken panel from white-screening the whole portal.
 *
 * The app previously had no error boundary at all, so a render error anywhere —
 * including inside a lazily loaded dashboard panel — unmounted the navigation
 * along with the page (docs/ux-design-review.md §3). Wrap each routed page in
 * its plugin's `alpha.tsx` loader.
 */
export class PageErrorBoundary extends Component<
  PageErrorBoundaryProps,
  PageErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error): PageErrorBoundaryState {
    return { error };
  }

  state: PageErrorBoundaryState = { error: null };

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Surfaced in the browser console and any attached error reporter; the user
    // gets the message itself through ErrorState below.
    // eslint-disable-next-line no-console
    console.error(
      'Unhandled error in portal page',
      error,
      errorInfo.componentStack,
    );
  }

  private readonly handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    const { title, children } = this.props;

    if (!error) return children;

    return (
      <div className="p-4 sm:p-6">
        <ErrorState
          title={
            title
              ? `${title} could not be displayed`
              : 'This page could not be displayed'
          }
          error={error}
          onRetry={this.handleReset}
          retryLabel="Reload this page"
        />
      </div>
    );
  }
}
